import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Kollez';
const SITE_URL = 'https://kollez.ai';
const DEFAULT_TITLE = 'Kollez — one platform for education';
const DEFAULT_DESCRIPTION =
  'One platform for education — where students learn and grow into emerging skills, partners find and grow talent, and institutions deliver more to every student.';
const DEFAULT_OG_IMAGE = '/og-card.png';

export interface PageMetaProps {
  /** Page-specific title. Rendered as `<title> · Kollez`. Omit on the homepage. */
  title?: string;
  /** One-sentence description. Falls back to the site description. */
  description?: string;
  /** Canonical path (starts with `/`). Falls back to `location.pathname`. */
  canonical?: string;
  /** Absolute or root-relative OG image path. */
  ogImage?: string;
  /** Optional JSON-LD structured data (any schema.org type). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Set to true to hide the page from search engines. */
  noindex?: boolean;
}

/**
 * Per-route metadata wrapper. Renders <title>, meta description, canonical,
 * OpenGraph, Twitter card, and optional JSON-LD structured data.
 *
 * Pair with a HelmetProvider mounted in main.tsx.
 *
 * Usage:
 *   <PageMeta
 *     title="Pricing"
 *     description="A plan for every placement cell. Free for every student."
 *     canonical="/pricing"
 *   />
 *
 * The site default title (`DEFAULT_TITLE`) is used on the homepage — pass
 * no `title` prop there. All other routes should pass a page-specific title.
 */
export function PageMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  jsonLd,
  noindex = false,
}: PageMetaProps) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : DEFAULT_TITLE;
  const url = canonical ? `${SITE_URL}${canonical}` : undefined;
  const ogUrl = ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`;
  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {url && <link rel="canonical" href={url} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={ogUrl} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogUrl} />
      {/* JSON-LD */}
      {jsonLdArray.map((data, i) => (
        <script key={`ld-${i}`} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * JSON-LD Organization schema — mount once on the homepage.
 * Google uses this for the brand knowledge panel.
 */
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.svg`,
  description: DEFAULT_DESCRIPTION,
  sameAs: [
    // Add real social links here as they become active.
  ],
} as const;
